import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import BlockIcon from '@mui/icons-material/Block';
import EventCheckInScanner, { CheckInStatsBar } from '../checkin/EventCheckInScanner';
import {
  createCheckInPortal,
  getCheckInPortalStatus,
  getParishCheckInSession,
  parishFetchCheckInStats,
  revokeCheckInPortal,
  updateCheckInPortalPassword,
  type CheckInPortalStatus,
  type CheckInStats,
} from '../../services/checkInService';

interface EventWorkspaceCheckInTabProps {
  eventId: string;
  eventTitle: string;
}

const statusLabel: Record<string, { label: string; color: 'default' | 'success' | 'warning' | 'error' }> = {
  none: { label: 'Não configurado', color: 'default' },
  active: { label: 'Ativo', color: 'success' },
  revoked: { label: 'Revogado', color: 'error' },
  expired: { label: 'Expirado', color: 'warning' },
};

const EventWorkspaceCheckInTab: React.FC<EventWorkspaceCheckInTabProps> = ({ eventId, eventTitle }) => {
  const [loading, setLoading] = useState(true);
  const [portal, setPortal] = useState<CheckInPortalStatus | null>(null);
  const [stats, setStats] = useState<CheckInStats | null>(null);
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showScanner, setShowScanner] = useState(false);
  const [passwordDialog, setPasswordDialog] = useState(false);

  const loadPortal = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCheckInPortalStatus(eventId);
      setPortal(data);
      setStats(data.stats || null);
    } catch {
      setError('Erro ao carregar status do portal.');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    void loadPortal();
  }, [loadPortal]);

  const refreshStats = useCallback(async () => {
    try {
      const data = await parishFetchCheckInStats(eventId);
      setStats(data);
    } catch {
      /* ignore */
    }
  }, [eventId]);

  const handleGenerate = async () => {
    if (password.length < 4) {
      setError('Senha deve ter pelo menos 4 caracteres.');
      return;
    }
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const data = await createCheckInPortal(eventId, password);
      setGeneratedUrl(data.url);
      setPassword('');
      setSuccess('Link gerado! Copie agora — ele não será exibido novamente.');
      await loadPortal();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao gerar link.');
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setSuccess('Copiado!');
    } catch {
      setError('Não foi possível copiar.');
    }
  };

  const handleRevoke = async () => {
    if (!window.confirm('Revogar o link de check-in? Voluntários perderão o acesso.')) return;
    setBusy(true);
    try {
      await revokeCheckInPortal(eventId);
      setGeneratedUrl(null);
      setSuccess('Portal revogado.');
      await loadPortal();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao revogar.');
    } finally {
      setBusy(false);
    }
  };

  const handleChangePassword = async () => {
    if (newPassword.length < 4) {
      setError('Senha deve ter pelo menos 4 caracteres.');
      return;
    }
    setBusy(true);
    try {
      await updateCheckInPortalPassword(eventId, newPassword);
      setNewPassword('');
      setPasswordDialog(false);
      setSuccess('Senha atualizada.');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao atualizar senha.');
    } finally {
      setBusy(false);
    }
  };

  const openCheckInScreen = async () => {
    try {
      await getParishCheckInSession(eventId);
      window.open(`/check-in?eventId=${eventId}&admin=1`, '_blank', 'noopener,noreferrer');
    } catch {
      setShowScanner(true);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const portalStatus = portal?.status || 'none';
  const statusMeta = statusLabel[portalStatus] || statusLabel.none;

  return (
    <Stack spacing={2.5}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      <Paper sx={{ p: 2.5, borderRadius: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1.5} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>Portal de check-in</Typography>
            <Typography variant="body2" color="text.secondary">
              Compartilhe o link com voluntários na entrada do evento. Eles precisarão da senha que você definir.
            </Typography>
          </Box>
          <Chip label={statusMeta.label} color={statusMeta.color} />
        </Stack>

        <CheckInStatsBar stats={stats} />

        {portal?.expiresAt && portalStatus !== 'none' && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            Expira em: {new Date(portal.expiresAt).toLocaleString('pt-BR')}
            {portal.revokedAt && ` · Revogado em ${new Date(portal.revokedAt).toLocaleString('pt-BR')}`}
          </Typography>
        )}

        <Grid container spacing={1.5}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              type="password"
              label="Senha para voluntários"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              helperText="Mínimo 4 caracteres. Usada no link compartilhado."
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ height: '100%', alignItems: { sm: 'flex-start' } }}>
              <Button
                variant="contained"
                startIcon={<QrCode2Icon />}
                disabled={busy || password.length < 4}
                onClick={() => void handleGenerate()}
              >
                {portal?.hasPortal ? 'Regenerar link' : 'Gerar link'}
              </Button>
              {portal?.hasPortal && (
                <>
                  <Button variant="outlined" onClick={() => setPasswordDialog(true)} disabled={busy}>
                    Trocar senha
                  </Button>
                  <Button variant="outlined" color="error" startIcon={<BlockIcon />} onClick={() => void handleRevoke()} disabled={busy}>
                    Revogar
                  </Button>
                </>
              )}
            </Stack>
          </Grid>
        </Grid>

        {generatedUrl && (
          <Paper variant="outlined" sx={{ p: 1.5, mt: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
            <Typography variant="caption" fontWeight={700} display="block" sx={{ mb: 0.5 }}>
              Link de check-in (copie agora):
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="body2" sx={{ wordBreak: 'break-all', flex: 1, fontFamily: 'monospace', fontSize: 12 }}>
                {generatedUrl}
              </Typography>
              <IconButton size="small" onClick={() => void handleCopy(generatedUrl)} aria-label="Copiar link">
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Paper>
        )}
      </Paper>

      <Paper sx={{ p: 2.5, borderRadius: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1.5}>
          <Box>
            <Typography variant="h6" fontWeight={700}>Tela de check-in</Typography>
            <Typography variant="body2" color="text.secondary">
              Use aqui no painel ou abra em outro dispositivo (sem precisar da senha do portal).
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button variant="outlined" startIcon={<OpenInNewIcon />} onClick={() => void openCheckInScreen()}>
              Abrir em nova aba
            </Button>
            <Button variant="contained" onClick={() => setShowScanner((v) => !v)}>
              {showScanner ? 'Ocultar scanner' : 'Check-in aqui'}
            </Button>
          </Stack>
        </Stack>

        {showScanner && (
          <Box sx={{ mt: 2 }}>
            <EventCheckInScanner
              eventId={eventId}
              parishMode
              onStatsRefresh={refreshStats}
            />
          </Box>
        )}
      </Paper>

      <Dialog open={passwordDialog} onClose={() => setPasswordDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Nova senha do portal</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            type="password"
            label="Nova senha"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={() => void handleChangePassword()} disabled={busy}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default EventWorkspaceCheckInTab;
